import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DynamicProfileImage from 'components/commons/DynamicProfileImage';
import styled, { css, keyframes } from 'styled-components';
import {
    MapPin, Phone, Share2, Instagram, Facebook, Youtube, Globe, Music,
    Wifi, CreditCard, Wind, Camera, Smile, Calendar, Coffee,
    Navigation, Star, ShoppingBag, Users, Thermometer, MessageCircle,
    UtensilsCrossed, Ticket, UserPlus, UserCheck, ArrowUpRight, Heart,
} from 'lucide-react';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import establishmentTypes from 'theme/establishmentTypes';
import eventTypes from 'theme/eventTypes';
import establishmentsApi from 'api/requests/establishments';
import useShare from 'hooks/useShare';
import useFollow from 'hooks/useFollow';
import { useAuth } from 'context/auth/Auth';
import { useShowLoginModal } from 'context/general/ShowLoginModal';

/*
 * Perfil público do estabelecimento.
 *
 * A pergunta que traz alguém aqui — vindo do mapa, da busca ou do link do
 * organizador embaixo de um evento — é sempre a mesma: "dá pra ir, e pra quê?".
 * Por isso a tela é organizada em torno de estado (aberto/fechado e quando
 * abre), do próximo evento e de como chegar/falar; o resto entra só quando
 * existe. Perfis sem produto, sem rede social e sem características são a
 * maioria, e a página precisa ficar inteira mesmo assim.
 */

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_SHORT = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
const DAY_FULL = ['segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado', 'domingo'];

const FEATURE_ICONS = {
    wifi: Wifi, smokingRoom: Wind, meetingRoom: Users, instagram: Instagram,
    bike: Navigation, cool: Thermometer, creditCard: CreditCard, parking: Navigation,
    music: Music, calendar: Calendar, heart: Heart, coffee: Coffee, smile: Smile,
    camera: Camera, phone: Phone, clock: Calendar,
};

/* A lista fechada que /establishments/options oferece hoje. Qualquer coisa fora
   dela cai no globo, mas nenhuma das seis chega sem ícone próprio. */
const SOCIAL_ICONS = {
    facebook: Facebook,
    youtube: Youtube,
    instagram: Instagram,
    site: Globe,
    whatsapp: MessageCircle,
    tiktok: Music,
};

function safeWebsiteHref(url) {
    if (!url) return null;
    if (/^javascript:/i.test(url)) return null;
    if (/^https?:\/\//i.test(url)) return url;
    return `https://${url}`;
}

/** 51990069850 → (51) 99006-9850. Fora do padrão brasileiro, devolve como veio. */
function formatPhone(raw) {
    const digits = String(raw || '').replace(/\D/g, '');
    if (digits.length === 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    if (digits.length === 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    return raw || '';
}

/* Só celular (11 dígitos com o 9) tem WhatsApp garantido. Oferecer o botão para
   um fixo manda a pessoa para uma conversa que não existe. */
function whatsappHref(raw) {
    const digits = String(raw || '').replace(/\D/g, '');
    if (digits.length !== 11) return null;
    return `https://wa.me/55${digits}`;
}

const toMinutes = (time) => {
    const [h, m] = String(time || '').split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
};

/**
 * Lê a agenda da semana e responde duas coisas: se hoje já está dentro de uma
 * janela (e até quando) e, se não, qual é a próxima abertura. É isso que
 * transforma um "Fechado" seco em "Fechado · abre sábado às 09:00".
 */
function readSchedule(dates, now = new Date()) {
    if (!dates) return null;

    const slotsOf = (index) => {
        const slots = dates[DAY_KEYS[index]];
        return Array.isArray(slots) ? slots.filter(slot => slot && (slot.start || slot['24h'])) : [];
    };

    const week = DAY_KEYS.map((_, index) => slotsOf(index));
    if (week.every(slots => slots.length === 0)) return null;

    // Date.getDay() começa no domingo; a agenda começa na segunda.
    const todayIndex = (now.getDay() + 6) % 7;
    const nowMinutes = now.getHours() * 60 + now.getMinutes();

    for (const slot of week[todayIndex]) {
        if (slot['24h']) return { week, todayIndex, closesAt: null, is24h: true, next: null };
        const start = toMinutes(slot.start);
        const end = slot.end ? toMinutes(slot.end) : 24 * 60;
        if (nowMinutes >= start && nowMinutes < end) {
            return { week, todayIndex, closesAt: slot.end || null, is24h: false, next: null };
        }
    }

    for (let offset = 0; offset < 7; offset++) {
        const index = (todayIndex + offset) % 7;
        for (const slot of week[index]) {
            if (slot['24h']) return { week, todayIndex, closesAt: null, is24h: false, next: { offset, index, time: null } };
            if (offset > 0 || toMinutes(slot.start) > nowMinutes) {
                return { week, todayIndex, closesAt: null, is24h: false, next: { offset, index, time: slot.start } };
            }
        }
    }

    return { week, todayIndex, closesAt: null, is24h: false, next: null };
}

function nextOpeningLabel(next) {
    if (!next) return null;
    const when = next.offset === 0 ? 'hoje' : next.offset === 1 ? 'amanhã' : DAY_FULL[next.index];
    return next.time ? `abre ${when} às ${next.time}` : `abre ${when}`;
}

/* Sete linhas de horário são sete linhas para ler. Agrupar dias com a mesma
   janela devolve o padrão da casa numa frase: "Sáb, Dom · 09:00 – 12:30". */
function groupSchedule(week) {
    const groups = [];
    week.forEach((slots, index) => {
        if (!slots.length) return;
        const label = slots.some(slot => slot['24h'])
            ? '24 horas'
            : slots.map(slot => `${slot.start}${slot.end ? ` – ${slot.end}` : ''}`).join(' e ');
        const last = groups[groups.length - 1];
        if (last && last.label === label) last.days.push(DAY_SHORT[index]);
        else groups.push({ label, days: [DAY_SHORT[index]] });
    });
    return groups;
}

function formatEventDate(dateString) {
    if (!dateString) return null;
    const date = new Date(`${dateString}T00:00:00`);
    if (Number.isNaN(date.getTime())) return null;
    return {
        day: String(date.getDate()).padStart(2, '0'),
        month: date.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', ''),
        weekday: date.toLocaleDateString('pt-BR', { weekday: 'long' }),
    };
}

export default function EstablishmentDetailV2({ data, slug: slugProp }) {
    const resolvedSlug = slugProp || data?.slug;
    const navigate = useNavigate();
    const [detail, setDetail] = useState(null);
    const [products, setProducts] = useState([]);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [linkCopied, setLinkCopied] = useState(false);
    const [descriptionOpen, setDescriptionOpen] = useState(false);
    const [actionsVisible, setActionsVisible] = useState(true);
    const actionsRef = useRef(null);
    const { share } = useShare();
    const linkCopiedTimerRef = useRef(null);
    const { isAuth, user } = useAuth();
    const { setShowLoginModal } = useShowLoginModal();
    const { isFollowing, toggleFollow } = useFollow(detail?.user_id, detail?.is_following);
    const isOwner = isAuth && user?.id === detail?.user_id;

    useEffect(() => {
        let isMounted = true;
        async function fetchData() {
            if (!resolvedSlug) return;
            try {
                // `options` saiu daqui junto com a lista de serviços não oferecidos:
                // era uma requisição inteira para desenhar chips apagados dizendo
                // o que a casa não faz.
                const [detailRes, productsRes, eventsRes] = await Promise.all([
                    establishmentsApi.getBySlug(resolvedSlug),
                    establishmentsApi.getProducts(resolvedSlug),
                    establishmentsApi.getUpcomingEvents(resolvedSlug),
                ]);
                if (!isMounted) return;
                setDetail(detailRes.data?.data || detailRes.data);
                setProducts(productsRes.data?.data || productsRes.data || []);
                setEvents(eventsRes.data?.data || eventsRes.data || []);
            } catch (err) {
                console.error('Error fetching establishment detail:', err);
            } finally {
                if (isMounted) setLoading(false);
            }
        }
        fetchData();
        return () => { isMounted = false; };
    }, [resolvedSlug]);

    useEffect(() => () => clearTimeout(linkCopiedTimerRef.current), []);

    /* A barra fixa do mobile só entra quando o botão da página sai de vista.
       As duas juntas na primeira dobra são a mesma ação oferecida duas vezes. */
    useEffect(() => {
        const node = actionsRef.current;
        if (!node || typeof IntersectionObserver === 'undefined') return;

        const observer = new IntersectionObserver(
            ([entry]) => setActionsVisible(entry.isIntersecting),
            { rootMargin: '-72px 0px 0px 0px' }
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [loading]);

    async function handleShare() {
        const result = await share({
            title: detail?.name || data?.name,
            text: `Conheça ${detail?.name || data?.name}`,
            url: window.location.href,
        });
        if (result?.method === 'clipboard' && result?.success) {
            clearTimeout(linkCopiedTimerRef.current);
            setLinkCopied(true);
            linkCopiedTimerRef.current = setTimeout(() => setLinkCopied(false), 2500);
        }
    }

    if (loading) return <DetailSkeleton />;

    const name = detail?.name || data?.name;
    const slug = detail?.slug || resolvedSlug;
    const profileImg = detail?.profileImage?.path || data?.profileImage?.path;
    const backgroundImg = detail?.backgroundImage?.path || data?.backgroundImage?.path;

    const addressLine = [detail?.address?.name, detail?.address?.number].filter(Boolean).join(', ');
    const addressArea = [detail?.address?.district, detail?.address?.city].filter(Boolean).join(', ');

    const classificationId = detail?.classification?.id;
    const isFree = classificationId != null ? establishmentTypes?.free?.id == classificationId : null;

    const schedule = readSchedule(detail?.dates);
    const isOpen = detail?.open;
    const openingLabel = nextOpeningLabel(schedule?.next);
    const scheduleGroups = schedule ? groupSchedule(schedule.week) : [];

    const phone = detail?.phone;
    const whatsapp = whatsappHref(phone);
    const deliveryUrl = safeWebsiteHref(detail?.delivery_url);
    const menuImage = detail?.menuImage?.path;
    const nextEvent = events?.[0];
    const followers = detail?.statistics?.followers;

    const socials = (detail?.socialMedia || [])
        .map(social => ({
            key: String(social.id),
            slug: social.icon?.toLowerCase(),
            Icon: SOCIAL_ICONS[social.icon?.toLowerCase()] || Globe,
            label: social.name,
            href: safeWebsiteHref(social.url),
        }))
        // A linha do telefone já leva ao WhatsApp e ainda mostra o número; a rede
        // social repetida deixava dois "WhatsApp" um embaixo do outro.
        .filter(social => social.href && !(whatsapp && social.slug === 'whatsapp'));

    /*
     * A ação principal segue o que a casa realmente tem, nesta ordem: ingresso do
     * próximo evento, cardápio, delivery, WhatsApp. Antes era sempre "Ver
     * Cardápio", que num estabelecimento sem produtos levava a uma tela vazia —
     * o botão mais destacado da página era o que menos entregava.
     */
    const primaryAction = (() => {
        if (nextEvent) {
            const paid = nextEvent?.classification?.name === eventTypes.paid.slug;
            return {
                label: paid ? 'Garantir ingresso' : 'Ver evento',
                href: `/evento/${nextEvent.slug}`,
                Icon: Ticket,
            };
        }
        if (products?.length) return { label: 'Ver cardápio', href: `/${slug}/menu`, Icon: UtensilsCrossed };
        if (menuImage) return { label: 'Ver cardápio', href: menuImage, Icon: UtensilsCrossed, external: true };
        if (deliveryUrl) return { label: 'Fazer pedido', href: deliveryUrl, Icon: ShoppingBag, external: true };
        if (whatsapp) return { label: 'Chamar no WhatsApp', href: whatsapp, Icon: MessageCircle, external: true };
        return null;
    })();

    const showsMenuAsSecondary = !!primaryAction && primaryAction.Icon !== UtensilsCrossed
        && (!!products?.length || !!menuImage);

    // A segunda coluna só existe quando há conteúdo para sustentar as duas. Sem
    // isso, um perfil sem produtos abria um vão branco do tamanho da tela.
    const isWide = (products?.length || 0) > 0
        || (events.length + socials.length + (detail?.features?.length ? 1 : 0)) >= 3;

    return (
        <Page>
            <Hero>
                <HeroImage
                    src={backgroundImg || '/assets/images/placeholder-background.png'}
                    alt=''
                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/assets/images/placeholder-background.png'; }}
                />
                <HeroScrim />
                <HeroInner $narrow={!isWide}>
                    <HeroTopActions>
                        {!isOwner && (
                            <GhostButton
                                type='button'
                                onClick={() => toggleFollow(() => setShowLoginModal(true))}
                                aria-pressed={isFollowing}
                                aria-label={isFollowing ? `Deixar de seguir ${name}` : `Seguir ${name}`}
                            >
                                {isFollowing ? <UserCheck size={15} /> : <UserPlus size={15} />}
                                <GhostLabel>{isFollowing ? 'Seguindo' : 'Seguir'}</GhostLabel>
                            </GhostButton>
                        )}
                        <GhostButton type='button' onClick={handleShare} aria-label={`Compartilhar ${name}`}>
                            <Share2 size={15} />
                            <GhostLabel>{linkCopied ? 'Link copiado' : 'Compartilhar'}</GhostLabel>
                        </GhostButton>
                    </HeroTopActions>

                    <Identity>
                        <Avatar>
                            <DynamicProfileImage img={profileImg} word={name} size='100%' textSize='title' />
                        </Avatar>
                        <IdentityText>
                            <EstablishmentName>{name}</EstablishmentName>
                            <StatusLine>
                                <StatusDot $open={!!isOpen} />
                                <StatusStrong>{isOpen ? 'Aberto agora' : 'Fechado'}</StatusStrong>
                                {isOpen && schedule?.closesAt && <StatusRest>· fecha às {schedule.closesAt}</StatusRest>}
                                {!isOpen && openingLabel && <StatusRest>· {openingLabel}</StatusRest>}
                            </StatusLine>
                            {(addressArea || addressLine) && (
                                <HeroAddress>{addressArea || addressLine}</HeroAddress>
                            )}
                        </IdentityText>
                    </Identity>
                </HeroInner>
            </Hero>

            <Container $narrow={!isWide}>
                {(isFree !== null || detail?.adulthood || detail?.tagsEstablishments?.length > 0 || followers > 0) && (
                    <TagsRow>
                        {/* "Gratuito" sozinho, no meio de Esporte e Beleza, não diz gratuito
                            o quê. Entre categorias, o rótulo precisa nomear o que classifica. */}
                        {isFree !== null && (
                            <Tag $tone={isFree ? 'free' : 'paid'}>
                                {isFree ? 'Entrada gratuita' : 'Entrada paga'}
                            </Tag>
                        )}
                        {detail?.adulthood && <Tag $tone='adult'>+18</Tag>}
                        {detail?.tagsEstablishments?.map(tag => (
                            <Tag key={tag.id ?? tag.name}>{tag.name}</Tag>
                        ))}
                        {followers > 0 && (
                            <FollowersTag>{followers} {followers === 1 ? 'seguidor' : 'seguidores'}</FollowersTag>
                        )}
                    </TagsRow>
                )}

                {detail?.description && (
                    <Description>
                        <DescriptionText $expanded={descriptionOpen}>{detail.description}</DescriptionText>
                        {detail.description.length > 220 && (
                            <TextButton type='button' onClick={() => setDescriptionOpen(open => !open)}>
                                {descriptionOpen ? 'Ver menos' : 'Ver mais'}
                            </TextButton>
                        )}
                    </Description>
                )}

                {(primaryAction || whatsapp || detail?.link_map) && (
                    <ActionsRow ref={actionsRef}>
                        {primaryAction && (
                            <PrimaryAction
                                href={primaryAction.href}
                                {...(primaryAction.external
                                    ? { target: '_blank', rel: 'noopener noreferrer' }
                                    : {})}
                            >
                                <primaryAction.Icon size={17} />
                                {primaryAction.label}
                            </PrimaryAction>
                        )}
                        {showsMenuAsSecondary && (
                            <SecondaryAction
                                href={products?.length ? `/${slug}/menu` : menuImage}
                                {...(products?.length ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
                            >
                                <UtensilsCrossed size={16} />
                                Ver cardápio
                            </SecondaryAction>
                        )}
                        {whatsapp && primaryAction?.href !== whatsapp && (
                            <SecondaryAction href={whatsapp} target='_blank' rel='noopener noreferrer'>
                                <MessageCircle size={16} />
                                WhatsApp
                            </SecondaryAction>
                        )}
                        {detail?.link_map && (
                            <SecondaryAction href={detail.link_map} target='_blank' rel='noopener noreferrer'>
                                <Navigation size={16} />
                                Como chegar
                            </SecondaryAction>
                        )}
                    </ActionsRow>
                )}

                <Layout $wide={isWide}>
                    <MainColumn>
                        {!!events.length && (
                            <Block $bare>
                                <BlockLabel>{events.length > 1 ? 'Próximos eventos' : 'Próximo evento'}</BlockLabel>
                                <EventList>
                                    {events.map(event => {
                                        const when = formatEventDate(event?.dates?.[0]?.date);
                                        const time = event?.dates?.[0]?.start?.slice(0, 5);
                                        const paid = event?.classification?.name === eventTypes.paid.slug;
                                        const street = event?.address?.street?.name;

                                        return (
                                            <EventCard key={event.slug} href={`/evento/${event.slug}`}>
                                                <EventDate>
                                                    <EventMonth>{when?.month || '--'}</EventMonth>
                                                    <EventDay>{when?.day || '--'}</EventDay>
                                                </EventDate>
                                                <EventBody>
                                                    <EventName>{event.name}</EventName>
                                                    <EventMeta>
                                                        {[when?.weekday, time].filter(Boolean).join(' · ')}
                                                    </EventMeta>
                                                    {!event?.hide_address && street && (
                                                        <EventMeta>
                                                            {[street, event.address.number && `nº ${event.address.number}`]
                                                                .filter(Boolean).join(', ')}
                                                        </EventMeta>
                                                    )}
                                                    <EventPrice $paid={paid}>{paid ? 'Ingresso pago' : 'Entrada gratuita'}</EventPrice>
                                                </EventBody>
                                                <ArrowUpRight size={18} />
                                            </EventCard>
                                        );
                                    })}
                                </EventList>
                            </Block>
                        )}

                        {!!products?.length && (
                            <Block>
                                <BlockLabel>Cardápio</BlockLabel>
                                <ProductsGrid>
                                    {products.slice(0, 6).map(product => (
                                        <ProductCard
                                            key={product?.id || product?.slug}
                                            type='button'
                                            onClick={() => navigate(`/estabelecimento/${slug}`)}
                                        >
                                            <ProductImage>
                                                {product?.image?.path && <img src={product.image.path} alt='' />}
                                            </ProductImage>
                                            <ProductInfo>
                                                <ProductName>{product?.name}</ProductName>
                                                {product?.category?.name && <ProductCategory>{product.category.name}</ProductCategory>}
                                                {product?.price != null && (
                                                    <ProductPrice>
                                                        R$ {Number(product.price).toFixed(2).replace('.', ',')}
                                                    </ProductPrice>
                                                )}
                                            </ProductInfo>
                                        </ProductCard>
                                    ))}
                                </ProductsGrid>
                                <TextButton as='a' href={`/${slug}/menu`}>Ver cardápio completo</TextButton>
                            </Block>
                        )}

                        {!!detail?.features?.length && (
                            <Block>
                                <BlockLabel>O que tem no local</BlockLabel>
                                <ChipsRow>
                                    {detail.features.map(feature => {
                                        const Icon = FEATURE_ICONS[feature.icon] || Star;
                                        return (
                                            <Chip key={feature.id ?? feature.name}>
                                                <Icon size={14} />
                                                {feature.name}
                                            </Chip>
                                        );
                                    })}
                                </ChipsRow>
                            </Block>
                        )}

                        {!!detail?.serviceOptions?.length && (
                            <Block>
                                <BlockLabel>Formas de atendimento</BlockLabel>
                                <ChipsRow>
                                    {detail.serviceOptions.map(service => (
                                        <Chip key={service.id ?? service.name}>{service.name}</Chip>
                                    ))}
                                </ChipsRow>
                            </Block>
                        )}
                    </MainColumn>

                    <SideColumn>
                        {schedule && (
                            <Block>
                                <BlockLabel>Quando abre</BlockLabel>
                                {/* A semana inteira num relance: o padrão da casa (fim de semana
                                    de manhã, todo dia à noite) é a informação que decide a
                                    visita, e sete linhas de tabela escondiam isso. */}
                                <WeekStrip role='list'>
                                    {schedule.week.map((slots, index) => (
                                        <DayCell
                                            key={DAY_KEYS[index]}
                                            role='listitem'
                                            $open={slots.length > 0}
                                            $today={index === schedule.todayIndex}
                                            aria-label={`${DAY_FULL[index]}: ${slots.length ? 'aberto' : 'fechado'}`}
                                        >
                                            {DAY_SHORT[index]}
                                        </DayCell>
                                    ))}
                                </WeekStrip>
                                <ScheduleList>
                                    {scheduleGroups.map(group => (
                                        <ScheduleLine key={group.days.join()}>
                                            <ScheduleDays>{group.days.join(', ')}</ScheduleDays>
                                            <ScheduleTime>{group.label}</ScheduleTime>
                                        </ScheduleLine>
                                    ))}
                                </ScheduleList>
                            </Block>
                        )}

                        {(detail?.address || phone || socials.length > 0 || deliveryUrl) && (
                            <Block>
                                <BlockLabel>Onde e como falar</BlockLabel>
                                <InfoList>
                                    {detail?.address && (
                                        <InfoRow as={detail?.link_map ? 'a' : 'div'}
                                            {...(detail?.link_map
                                                ? { href: detail.link_map, target: '_blank', rel: 'noopener noreferrer' }
                                                : {})}
                                        >
                                            <MapPin size={17} />
                                            <InfoBody>
                                                <InfoStrong>
                                                    {addressLine}{detail.address?.complement ? ` — ${detail.address.complement}` : ''}
                                                </InfoStrong>
                                                {addressArea && <InfoSub>{addressArea}</InfoSub>}
                                            </InfoBody>
                                            {detail?.link_map && <ArrowUpRight size={16} />}
                                        </InfoRow>
                                    )}
                                    {phone && (
                                        <InfoRow as='a' href={whatsapp || `tel:+55${String(phone).replace(/\D/g, '')}`}
                                            {...(whatsapp ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                                        >
                                            <Phone size={17} />
                                            <InfoBody>
                                                <InfoStrong>{formatPhone(phone)}</InfoStrong>
                                                <InfoSub>{whatsapp ? 'Chamar no WhatsApp' : 'Ligar'}</InfoSub>
                                            </InfoBody>
                                            <ArrowUpRight size={16} />
                                        </InfoRow>
                                    )}
                                    {deliveryUrl && (
                                        <InfoRow as='a' href={deliveryUrl} target='_blank' rel='noopener noreferrer'>
                                            <ShoppingBag size={17} />
                                            <InfoBody>
                                                <InfoStrong>Fazer pedido</InfoStrong>
                                                <InfoSub>Site de delivery</InfoSub>
                                            </InfoBody>
                                            <ArrowUpRight size={16} />
                                        </InfoRow>
                                    )}
                                    {socials.map(social => (
                                        <InfoRow key={social.key} as='a' href={social.href} target='_blank' rel='noopener noreferrer'>
                                            <social.Icon size={17} />
                                            <InfoBody>
                                                <InfoStrong>{social.label}</InfoStrong>
                                            </InfoBody>
                                            <ArrowUpRight size={16} />
                                        </InfoRow>
                                    ))}
                                </InfoList>
                            </Block>
                        )}
                    </SideColumn>
                </Layout>
            </Container>

            {primaryAction && !actionsVisible && (
                <MobileBar>
                    <PrimaryAction
                        href={primaryAction.href}
                        {...(primaryAction.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    >
                        <primaryAction.Icon size={17} />
                        {primaryAction.label}
                    </PrimaryAction>
                </MobileBar>
            )}
        </Page>
    );
}

function DetailSkeleton() {
    return (
        <Page aria-busy='true'>
            <SkeletonHero />
            <Container>
                <SkeletonLine style={{ width: '40%', height: 18, marginTop: 20 }} />
                <SkeletonLine style={{ width: '75%', marginTop: 14 }} />
                <SkeletonLine style={{ width: '60%', marginTop: 8 }} />
                <SkeletonLine style={{ width: 220, height: 46, borderRadius: 24, marginTop: 22 }} />
                <SkeletonLine style={{ width: '100%', height: 120, borderRadius: 14, marginTop: 28 }} />
                <SkeletonLine style={{ width: '100%', height: 160, borderRadius: 14, marginTop: 16 }} />
            </Container>
        </Page>
    );
}

/* ── design tokens ──────────────────────────────────────────────────────────
 * O roxo saiu de todos os títulos e ficou reservado para a ação principal e o
 * dia de hoje. Sem isso a página inteira gritava no mesmo tom e nada se
 * destacava. A hierarquia agora é feita com tinta, peso e espaço.
 * Roboto é carregado em 400/500/600 — 700 vira negrito sintético, então o peso
 * máximo aqui é 600.
 */
const ink = colors.nightDark;
const ink2 = '#5B5B5B';
const ink3 = '#8C8C8C';
const line = 'rgba(26, 26, 26, 0.10)';
const accent = colors.secondaryPurple;
const accentSoft = 'rgba(128, 37, 232, 0.08)';

const rise = keyframes`
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
    0%, 100% { opacity: 1; }
    50%      { opacity: 0.55; }
`;

const Page = styled.div`
    width: 100%;
    background: ${colors.neutralBackground};
    min-height: 100%;
    padding-bottom: 96px;

    @media screen and (min-width: ${breakpoints.lg}) {
        padding-bottom: 56px;
    }
`;

/*
 * Uma medida só para a página inteira. Num perfil magro, manter a régua de
 * 1080px deixava a coluna de 680px encostada na esquerda com meia tela vazia à
 * direita; estreitando o container, capa, nome, ações e blocos passam a dividir
 * o mesmo eixo e a página lê como um documento.
 */
const Container = styled.div`
    width: 100%;
    max-width: ${({ $narrow }) => $narrow ? '780px' : '1080px'};
    margin: 0 auto;
    padding: 0 20px;
`;

/* ── hero ── */
const Hero = styled.header`
    position: relative;
    width: 100%;
    height: 300px;
    background: ${colors.secondary};
    overflow: hidden;
    display: flex;

    @media screen and (max-width: calc(${breakpoints.lg} - 1px)) {
        height: 260px;
    }
`;

const HeroImage = styled.img`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

/* Duas paradas quase opacas embaixo: o nome precisa aguentar qualquer foto de
   capa, inclusive as claras. */
const HeroScrim = styled.div`
    position: absolute;
    inset: 0;
    background: linear-gradient(
        to bottom,
        rgba(10, 8, 20, 0.45) 0%,
        rgba(10, 8, 20, 0.20) 34%,
        rgba(10, 8, 20, 0.72) 78%,
        rgba(10, 8, 20, 0.88) 100%
    );
`;

const HeroInner = styled.div`
    position: relative;
    width: 100%;
    max-width: ${({ $narrow }) => $narrow ? '780px' : '1080px'};
    margin: 0 auto;
    padding: 16px 20px 22px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    animation: ${rise} 0.5s cubic-bezier(0.22, 1, 0.36, 1) both;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

const HeroTopActions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
`;

const GhostButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.45);
    background: rgba(12, 10, 20, 0.35);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    color: ${colors.neutralLight};
    font-size: 13px;
    font-weight: 500;
    transition: background 0.18s ease, border-color 0.18s ease;

    &:hover { background: rgba(12, 10, 20, 0.6); border-color: rgba(255, 255, 255, 0.8); }
    &:focus-visible { outline: 2px solid ${colors.neutralLight}; outline-offset: 2px; }
`;

const GhostLabel = styled.span`
    @media screen and (max-width: 400px) {
        display: none;
    }
`;

const Identity = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 16px;
`;

const Avatar = styled.div`
    width: 92px;
    height: 92px;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
    background: ${colors.neutralLight};
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.9), 0 6px 20px rgba(0, 0, 0, 0.3);

    @media screen and (max-width: calc(${breakpoints.lg} - 1px)) {
        width: 74px;
        height: 74px;
    }
`;

const IdentityText = styled.div`
    min-width: 0;
    padding-bottom: 4px;
`;

const EstablishmentName = styled.h1`
    color: ${colors.neutralLight};
    font-size: 34px;
    font-weight: 600;
    letter-spacing: -0.02em;
    line-height: 1.1;
    margin-bottom: 8px;
    text-shadow: 0 1px 12px rgba(0, 0, 0, 0.35);

    @media screen and (max-width: calc(${breakpoints.lg} - 1px)) {
        font-size: 24px;
    }
`;

const StatusLine = styled.p`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    font-size: 14.5px;
    color: rgba(255, 255, 255, 0.9);
`;

const StatusDot = styled.span`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${({ $open }) => $open ? colors.success : 'rgba(255, 255, 255, 0.55)'};
    box-shadow: ${({ $open }) => $open ? `0 0 0 3px rgba(43, 200, 102, 0.28)` : 'none'};
`;

const StatusStrong = styled.span`
    font-weight: 500;
    color: ${colors.neutralLight};
`;

const StatusRest = styled.span`
    color: rgba(255, 255, 255, 0.78);
`;

const HeroAddress = styled.p`
    margin-top: 4px;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.7);
`;

/* ── tags, descrição, ações ── */
const TagsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
    padding-top: 22px;
`;

const Tag = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 5px 12px;
    border-radius: 999px;
    font-size: 12.5px;
    line-height: 1.3;
    background: ${colors.neutralLight};
    border: 1px solid ${line};
    color: ${ink2};

    ${({ $tone }) => $tone === 'free' && css`
        background: rgba(39, 183, 93, 0.10);
        border-color: rgba(39, 183, 93, 0.22);
        color: ${colors.successDark};
        font-weight: 500;
    `}
    ${({ $tone }) => $tone === 'paid' && css`
        background: ${accentSoft};
        border-color: rgba(128, 37, 232, 0.20);
        color: ${accent};
        font-weight: 500;
    `}
    ${({ $tone }) => $tone === 'adult' && css`
        background: ${ink};
        border-color: ${ink};
        color: ${colors.neutralLight};
        font-weight: 500;
    `}
`;

const FollowersTag = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 5px 4px;
    font-size: 12.5px;
    color: ${ink3};
`;

const Description = styled.div`
    padding-top: 16px;
    max-width: 680px;
`;

const DescriptionText = styled.p`
    font-size: 15px;
    line-height: 1.65;
    color: ${ink2};
    white-space: pre-line;

    ${({ $expanded }) => !$expanded && css`
        display: -webkit-box;
        -webkit-line-clamp: 4;
        -webkit-box-orient: vertical;
        overflow: hidden;
    `}
`;

const TextButton = styled.button`
    display: inline-flex;
    align-items: center;
    margin-top: 8px;
    padding: 4px 0;
    background: none;
    color: ${accent};
    font-size: 14px;
    font-weight: 500;
    text-decoration: none;

    &:hover { text-decoration: underline; }
    &:focus-visible { outline: 2px solid ${accent}; outline-offset: 3px; border-radius: 2px; }
`;

const ActionsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    padding-top: 22px;
`;

const PrimaryAction = styled.a`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    height: 46px;
    padding: 0 26px;
    border-radius: 999px;
    background: ${accent};
    color: ${colors.neutralLight};
    font-size: 15px;
    font-weight: 500;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
    box-shadow: 0 4px 16px rgba(128, 37, 232, 0.28);

    &:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(128, 37, 232, 0.34); }
    &:focus-visible { outline: 2px solid ${ink}; outline-offset: 3px; }

    @media (prefers-reduced-motion: reduce) {
        transition: none;
        &:hover { transform: none; }
    }
`;

const SecondaryAction = styled.a`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 46px;
    padding: 0 20px;
    border-radius: 999px;
    border: 1px solid ${line};
    background: ${colors.neutralLight};
    color: ${ink};
    font-size: 14.5px;
    font-weight: 500;
    transition: border-color 0.15s ease, background 0.15s ease;

    &:hover { border-color: ${ink3}; background: ${colors.neutral}; }
    &:focus-visible { outline: 2px solid ${accent}; outline-offset: 3px; }

    svg { color: ${ink2}; }
`;

/* ── layout ── */
const Layout = styled.div`
    display: grid;
    gap: 20px;
    padding-top: 30px;
    align-items: start;

    ${({ $wide }) => $wide
        ? css`
            grid-template-columns: 1fr;

            @media screen and (min-width: ${breakpoints.lg}) {
                grid-template-columns: minmax(0, 1fr) 360px;
            }
        `
        : css`
            grid-template-columns: minmax(0, 1fr);
        `}
`;

const MainColumn = styled.div`
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 20px;

    &:empty { display: none; }
`;

const SideColumn = styled.div`
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 20px;

    &:empty { display: none; }
`;

const Block = styled.section`
    background: ${colors.neutralLight};
    border: 1px solid ${line};
    border-radius: 16px;
    padding: 20px;

    /* Os eventos já são cards; embrulhá-los numa caixa branca criava retângulo
       dentro de retângulo e achatava justamente o que a página quer destacar. */
    ${({ $bare }) => $bare && css`
        background: none;
        border: none;
        padding: 0;
    `}
`;

const BlockLabel = styled.h2`
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.10em;
    text-transform: uppercase;
    color: ${ink3};
    margin-bottom: 14px;
`;

/* ── eventos ── */
const EventList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const EventCard = styled.a`
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px;
    border: 1px solid ${line};
    border-radius: 16px;
    background: ${colors.neutralLight};
    transition: border-color 0.15s ease, background 0.15s ease;

    &:hover { border-color: ${accent}; background: ${accentSoft}; }
    &:focus-visible { outline: 2px solid ${accent}; outline-offset: 2px; }

    > svg { color: ${ink3}; flex-shrink: 0; }
`;

/* O bloco de data substitui a miniatura: numa lista de eventos da mesma casa, a
   arte se repete e o que diferencia é o dia. */
const EventDate = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 62px;
    flex-shrink: 0;
    border-radius: 10px;
    background: ${accentSoft};
    border: 1px solid rgba(128, 37, 232, 0.16);
`;

const EventMonth = styled.span`
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: ${accent};
`;

const EventDay = styled.span`
    font-size: 24px;
    font-weight: 600;
    line-height: 1.1;
    color: ${ink};
`;

const EventBody = styled.div`
    flex: 1;
    min-width: 0;
`;

const EventName = styled.span`
    display: block;
    font-size: 15px;
    font-weight: 500;
    color: ${ink};
    margin-bottom: 3px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const EventMeta = styled.span`
    display: block;
    font-size: 12.5px;
    color: ${ink3};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &::first-letter { text-transform: uppercase; }
`;

const EventPrice = styled.span`
    display: inline-block;
    margin-top: 6px;
    font-size: 12px;
    font-weight: 500;
    color: ${({ $paid }) => $paid ? accent : colors.successDark};
`;

/* ── semana ── */
const WeekStrip = styled.div`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 5px;
`;

const DayCell = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    border-radius: 9px;
    font-size: 12px;
    font-weight: 500;
    border: 1px solid ${line};
    background: ${colors.neutral};
    color: ${ink3};

    ${({ $open }) => $open && css`
        background: ${accentSoft};
        border-color: rgba(128, 37, 232, 0.22);
        color: ${accent};
    `}

    /* Hoje é referência, não destaque: um anel por dentro marca o dia sem
       competir com os dias em que a casa realmente abre. */
    ${({ $today }) => $today && css`
        box-shadow: inset 0 0 0 1.5px ${ink2};
        color: ${ink};
    `}
`;

const ScheduleList = styled.div`
    margin-top: 14px;
    display: flex;
    flex-direction: column;
    gap: 7px;
`;

const ScheduleLine = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    font-size: 13.5px;
`;

const ScheduleDays = styled.span`
    color: ${ink2};
`;

const ScheduleTime = styled.span`
    color: ${ink};
    font-weight: 500;
    font-variant-numeric: tabular-nums;
`;

/* ── contato ── */
const InfoList = styled.div`
    display: flex;
    flex-direction: column;
`;

const InfoRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 0;
    border-bottom: 1px solid ${line};
    color: ${ink};

    &:first-child { padding-top: 0; }
    &:last-child { border-bottom: none; padding-bottom: 0; }

    > svg { color: ${ink3}; flex-shrink: 0; }
    &:hover > svg:last-child { color: ${accent}; }
    &:focus-visible { outline: 2px solid ${accent}; outline-offset: 2px; border-radius: 6px; }
`;

const InfoBody = styled.div`
    flex: 1;
    min-width: 0;
`;

const InfoStrong = styled.span`
    display: block;
    font-size: 14.5px;
    color: ${ink};
`;

const InfoSub = styled.span`
    display: block;
    margin-top: 2px;
    font-size: 12.5px;
    color: ${ink3};
`;

/* ── chips ── */
const ChipsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const Chip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 8px 14px;
    border-radius: 999px;
    border: 1px solid ${line};
    background: ${colors.neutral};
    font-size: 13px;
    color: ${ink2};

    svg { color: ${ink3}; }
`;

/* ── produtos ── */
const ProductsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;

    @media screen and (max-width: ${breakpoints.md}) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const ProductCard = styled.button`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px;
    text-align: left;
    border: 1px solid ${line};
    border-radius: 12px;
    background: ${colors.neutralLight};
    transition: border-color 0.15s ease;

    &:hover { border-color: ${ink3}; }
    &:focus-visible { outline: 2px solid ${accent}; outline-offset: 2px; }
`;

const ProductImage = styled.div`
    width: 54px;
    height: 54px;
    flex-shrink: 0;
    border-radius: 9px;
    overflow: hidden;
    background: ${colors.neutralDark};

    img { width: 100%; height: 100%; object-fit: cover; }
`;

const ProductInfo = styled.div`
    min-width: 0;
`;

const ProductName = styled.span`
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: ${ink};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ProductCategory = styled.span`
    display: block;
    font-size: 12px;
    color: ${ink3};
    margin: 1px 0 3px;
`;

const ProductPrice = styled.span`
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: ${accent};
    font-variant-numeric: tabular-nums;
`;

/* ── barra fixa no mobile ── */
const MobileBar = styled.div`
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border-top: 1px solid ${line};
    z-index: 20;
    animation: ${rise} 0.22s ease-out both;

    a { width: 100%; }

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        display: none;
    }
`;

/* ── esqueleto ── */
const SkeletonHero = styled.div`
    width: 100%;
    height: 300px;
    background: ${colors.neutralDark};
    animation: ${pulse} 1.6s ease-in-out infinite;

    @media screen and (max-width: calc(${breakpoints.lg} - 1px)) {
        height: 260px;
    }

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

const SkeletonLine = styled.div`
    height: 13px;
    border-radius: 6px;
    background: ${colors.neutralDark};
    animation: ${pulse} 1.6s ease-in-out infinite;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;
